<template>
  <div v-if="missedTransactionQuery" class="w-full h-full space-y-4">
    <Drawer
      direction="right"
      :open="showFilters"
      @update:open="(o) => { if (!o) { showFilters = false; offerType = []; retailerCategory = undefined } }"
    >
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Add action</DrawerTitle>
          <DrawerDescription>Add an action to resolve this query</DrawerDescription>
        </DrawerHeader>
        <div class="grid gap-4 px-4">
          <div class="grid gap-1.5">
            <label class="text-sm font-medium" for="sa-actions">Select action</label>
            <Select v-model="supportAction.actions">
              <SelectTrigger id="sa-actions" class="w-full">
                <SelectValue placeholder="Select action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="o in orderByList" :key="o.id" :value="o.id">{{ o.text }}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div class="grid gap-1.5">
            <label class="text-sm font-medium" for="sa-notes">Notes</label>
            <Input id="sa-notes" v-model="supportAction.notes" placeholder="Add any context for this action" />
          </div>
        </div>
        <DrawerFooter>
          <Button
            :disabled="!supportAction.actions"
            @click="() => { showFilters = false; addSupportTicketAction() }"
          >Submit action</Button>
          <Button
            variant="outline"
            @click="() => { showFilters = false; offerType = []; retailerCategory = undefined }"
          >Cancel</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    <section>
      <div class="flex items-start justify-between gap-4">
        <div class="space-y-1">
          <div class="flex items-center gap-2">
            <h2 class="text-lg font-semibold tracking-tight">Support ticket</h2>
            <Badge :variant="missedTransactionQuery.resolved ? 'success' : 'warning'">
              {{ missedTransactionQuery.resolved ? 'Resolved' : 'Open' }}
            </Badge>
          </div>
          <!-- text-xs so the label matches the id component sitting next to it. -->
          <div class="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span>Ticket ID</span>
            <CopyId :value="missedTransactionQuery.id" :max-width="220" />
          </div>
        </div>
        <!-- "Add action" lives on the Actions log card; a second copy here was duplicate. -->
      </div>
    </section>

    <Tabs default-value="details">
      <TabsList class="w-full">
        <TabsTrigger value="details">Details</TabsTrigger>
        <TabsTrigger value="offer">Expected offer</TabsTrigger>
        <TabsTrigger value="data">Data</TabsTrigger>
      </TabsList>

      <TabsContent value="details" class="space-y-4">
    <Card>
      <CardHeader>
        <CardTitle>Ticket details</CardTitle>
      </CardHeader>
      <CardContent>
        <dl class="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <dt class="text-xs uppercase tracking-wide text-muted-foreground">Ticket ID</dt>
            <dd class="mt-1"><CopyId :value="missedTransactionQuery.id" :max-width="180" /></dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-muted-foreground">User ID</dt>
            <dd class="mt-1"><CopyId :value="missedTransactionQuery.endUserId" :to="`/enrolled-users/users/${missedTransactionQuery.endUserId}`" :max-width="180" /></dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-muted-foreground">Transaction ID</dt>
            <dd class="mt-1"><CopyId :value="missedTransactionQuery.transactionId" :to="{ path: '/cashback-program/ledger', query: { search: missedTransactionQuery.transactionId } }" :max-width="180" /></dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-muted-foreground">Expected offer ID</dt>
            <dd class="mt-1"><CopyId :value="missedTransactionQuery.programOfferRequested.id" :to="`/retailer/${expectedOffer.retailer.id}/offer/${expectedOffer.offer.id}`" :max-width="180" /></dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-muted-foreground">Support level</dt>
            <dd class="mt-1 text-sm">{{ missedTransactionQuery.supportLevel }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-muted-foreground">Status</dt>
            <dd class="mt-1">
              <Badge :variant="missedTransactionQuery.resolved ? 'success' : 'warning'">{{ missedTransactionQuery.resolved ? 'Resolved' : 'Open' }}</Badge>
            </dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-muted-foreground">Created on</dt>
            <dd class="mt-1 text-sm">{{ niceDateTime(missedTransactionQuery.createdOn) }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-muted-foreground">Updated on</dt>
            <dd class="mt-1 text-sm">{{ missedTransactionQuery.updatedOn != null ? niceDateTime(missedTransactionQuery.updatedOn) : '—' }}</dd>
          </div>
          <div>
            <dt class="text-xs uppercase tracking-wide text-muted-foreground">Resolved on</dt>
            <dd class="mt-1 text-sm">{{ missedTransactionQuery.resolvedOn != null ? niceDateTime(missedTransactionQuery.resolvedOn) : '—' }}</dd>
          </div>
        </dl>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle>Message</CardTitle>
      </CardHeader>
      <CardContent>
        <p class="text-sm">{{ missedTransactionQuery.endUserMessage }}</p>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle>Actions log</CardTitle>
        <CardDescription>View actions taken on this ticket (Latest action first)</CardDescription>
        <CardAction class="flex items-center gap-2">
          <Button id="filterButton" size="sm" variant="outline" @click="downloadLogs()">
            <Download class="w-4 h-4" />Download logs
          </Button>
          <Button
            id="createContract"
            size="sm"
            @click="showFilters = true"
          >
            <Plus class="w-4 h-4" />Add action
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <!-- The action log is a client-side array (built from the ticket's
             actionLog map), so it paginates client-side and must not bind the
             shared loading/error flags — a failed fetch elsewhere was blanking
             it out. -->
        <DataTable :headers="headersTable1" :items="actionLog">
          <template #item-action="row">
            <Badge variant="secondary">{{ actionLabel(row.action) }}</Badge>
          </template>
          <template #empty-message>
            <p class="text-sm text-muted-foreground py-6 text-center">No actions on this ticket yet.</p>
          </template>
        </DataTable>
      </CardContent>
    </Card>
      </TabsContent>

      <TabsContent value="offer">
    <Card>
      <CardHeader>
        <CardTitle>Expected offer data</CardTitle>
        <CardAction>
          <Button
            size="sm"
            variant="outline"
            @click="openOfferInNewTab(expectedOffer.retailer.id, expectedOffer.offer.id)"
          >View offer</Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <div>
          <div class="flex items-center gap-3">
            <img
              v-if="expectedOffer.retailer.logo"
              :src="expectedOffer.retailer.logo"
              class="w-10 h-10 rounded-md object-contain border bg-white shrink-0"
              alt=""
            />
            <Store v-else class="w-6 h-6 text-muted-foreground" />
            <div class="min-w-0">
              <p class="text-sm font-semibold truncate">{{ expectedOffer.retailer.retailerName }}</p>
              <div class="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span>Offer ID</span>
                <CopyId :value="expectedOffer.id" :max-width="160" />
              </div>
            </div>
          </div>

          <p class="mt-6 mb-3 text-sm font-semibold text-foreground">Offer information</p>

          <!-- Same shape as the Cashback conditions block below: outlined card, 4 across. -->
          <div class="rounded-lg border p-4">
          <dl class="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt class="text-xs uppercase tracking-wide text-muted-foreground">Offer type</dt>
              <dd class="mt-1"><Badge variant="secondary">{{ formatOfferType(expectedOffer.offer.offerType) }}</Badge></dd>
            </div>
            <div>
              <dt class="text-xs uppercase tracking-wide text-muted-foreground">Status</dt>
              <dd class="mt-1">
                <Badge v-if="expectedOffer.active" variant="success">Live</Badge>
                <Badge v-else variant="warning">Deactivated</Badge>
              </dd>
            </div>
            <div>
              <dt class="text-xs uppercase tracking-wide text-muted-foreground">Approval</dt>
              <dd class="mt-1">
                <Badge v-if="expectedOffer.offerApproval === 'APPROVED'" variant="success">Approved</Badge>
                <Badge v-else-if="expectedOffer.offerApproval === 'PENDING'" variant="warning">Pending approval</Badge>
                <Badge v-else-if="expectedOffer.offerApproval === 'DECLINED'" variant="destructive">Approval declined</Badge>
                <span v-else class="text-muted-foreground">—</span>
              </dd>
            </div>
            <div>
              <dt class="text-xs uppercase tracking-wide text-muted-foreground">Live date</dt>
              <dd class="mt-1 text-sm">{{ niceDateTime(expectedOffer.createdOn) }}</dd>
            </div>
            <div>
              <dt class="text-xs uppercase tracking-wide text-muted-foreground">Country</dt>
              <dd class="mt-1 text-sm">{{ expectedOffer.offer.country || '—' }}</dd>
            </div>
            <div>
              <dt class="text-xs uppercase tracking-wide text-muted-foreground">Currency</dt>
              <dd class="mt-1 text-sm">{{ (expectedOffer.offer.currencyList && expectedOffer.offer.currencyList.join(', ')) || '—' }}</dd>
            </div>
            <div>
              <dt class="text-xs uppercase tracking-wide text-muted-foreground">Match against</dt>
              <dd class="mt-1 text-sm">{{ expectedOffer.offer.validFor === 'ALL_CHANNELS' ? 'ALL_TRANSACTIONS' : expectedOffer.offer.validFor }}</dd>
            </div>
            <div>
              <dt class="text-xs uppercase tracking-wide text-muted-foreground">OB compatible</dt>
              <dd class="mt-1">
                <Badge :variant="expectedOffer.offer.obPublisherCompatible ? 'success' : 'secondary'">{{ expectedOffer.offer.obPublisherCompatible ? 'Yes' : 'No' }}</Badge>
              </dd>
            </div>
            <div>
              <dt class="text-xs uppercase tracking-wide text-muted-foreground">Available for</dt>
              <dd class="mt-1 text-sm">{{ expectedOffer.customerTierType }}</dd>
            </div>
            <div>
              <dt class="text-xs uppercase tracking-wide text-muted-foreground">Spending cap</dt>
              <dd class="mt-1 text-sm tabular-nums">
                <span v-if="expectedOffer.offer.spendingCapped || expectedOffer.offer.spendingCapAmount > 0">
                  {{ getCurrencySymbol(expectedOffer.offer.currencyList) + expectedOffer.offer.spendingCapAmount }}
                </span>
                <span v-else class="text-muted-foreground">No cap applied</span>
              </dd>
            </div>
            <div v-if="expectedOffer.selectedCustomerTiers && expectedOffer.selectedCustomerTiers.length" class="sm:col-span-2 lg:col-span-4">
              <dt class="text-xs uppercase tracking-wide text-muted-foreground">Tiers selected</dt>
              <dd class="mt-1 flex flex-wrap gap-1">
                <Badge v-for="(tier, i) in expectedOffer.selectedCustomerTiers" :key="i" variant="outline">{{ tier }}</Badge>
              </dd>
            </div>
          </dl>
          </div>

          <p class="mt-6 mb-3 text-sm font-semibold text-foreground">Cashback conditions per transaction</p>

          <div class="space-y-3">
            <div
              v-for="(earning, ei) in expectedOffer.cashbackEarnings"
              :key="ei"
              class="rounded-lg border p-4"
            >
              <template v-if="earning.conditions.length === 1">
                <dl class="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <dt class="text-xs uppercase tracking-wide text-muted-foreground">Total cashback</dt>
                    <dd class="mt-1 text-sm tabular-nums">
                      <span v-if="earning.conditions[0].value.maxAvailableValue != null">{{ formatRate(earning, earning.conditions[0].value.maxAvailableValue) }}</span>
                      <span v-else class="text-xs text-muted-foreground">Online offers get a split of commission</span>
                    </dd>
                  </div>
                  <div>
                    <dt class="text-xs uppercase tracking-wide text-muted-foreground">User cashback</dt>
                    <dd class="mt-1">
                      <Badge v-if="earning.conditions[0].value.endUserValue == null" variant="warning">Please set</Badge>
                      <span v-else class="text-sm tabular-nums">{{ formatRate(earning, earning.conditions[0].value.endUserValue) }}</span>
                    </dd>
                  </div>
                  <div>
                    <dt class="text-xs uppercase tracking-wide text-muted-foreground">Your commission</dt>
                    <dd class="mt-1">
                      <Badge v-if="earning.conditions[0].value.clientValue == null" variant="warning">Please set</Badge>
                      <span v-else class="text-sm tabular-nums">{{ formatRate(earning, earning.conditions[0].value.clientValue) }}</span>
                    </dd>
                  </div>
                  <div>
                    <dt class="text-xs uppercase tracking-wide text-muted-foreground">Cashback type</dt>
                    <dd class="mt-1"><Badge variant="secondary">{{ earning.conditions[0].value.type }}</Badge></dd>
                  </div>
                  <div>
                    <dt class="text-xs uppercase tracking-wide text-muted-foreground">Customer type</dt>
                    <dd class="mt-1"><Badge variant="secondary">{{ earning.customerType }}</Badge></dd>
                  </div>
                  <div>
                    <dt class="text-xs uppercase tracking-wide text-muted-foreground">Use type</dt>
                    <dd class="mt-1"><Badge variant="secondary">{{ earning.offerUsageType }}</Badge></dd>
                  </div>
                  <div>
                    <dt class="text-xs uppercase tracking-wide text-muted-foreground">Fixed split</dt>
                    <dd class="mt-1">
                      <Badge v-if="earning.conditions[0].fixedDistributorCommission" variant="success">True</Badge>
                      <Badge v-else variant="destructive">False</Badge>
                    </dd>
                  </div>
                  <div>
                    <dt class="text-xs uppercase tracking-wide text-muted-foreground">Spend condition</dt>
                    <dd class="mt-1 text-sm">{{ spendCondition(earning) }}</dd>
                  </div>
                </dl>
              </template>
              <p v-else class="text-sm text-muted-foreground">Multiple conditions configured for this rule.</p>
            </div>
            <p v-if="!expectedOffer.cashbackEarnings || !expectedOffer.cashbackEarnings.length" class="text-sm text-muted-foreground">No cashback conditions configured.</p>
          </div>

          <p class="mt-6 mb-3 text-sm font-semibold text-foreground">Available channels</p>

          <DataTable
            :headers="channelsTable"
            :items="expectedOffer.offer.channels"
            :loading="loading"
            :error="error"
            @retry="getSupportTicket(ticketId)"
            :hide-footer="true"
          >
          </DataTable>
        </div>
      </CardContent>
    </Card>
      </TabsContent>

      <TabsContent value="data">
        <Tabs v-model="selectedDataItem">
          <TabsList class="mb-4">
            <TabsTrigger value="TRANSACTION_DATA">Transaction data</TabsTrigger>
            <TabsTrigger value="OFFER_MATCHES">New offer matches</TabsTrigger>
            <TabsTrigger value="CASHBACK_DATA">Cashback data</TabsTrigger>
            <TabsTrigger value="END_USER_DATA">User data</TabsTrigger>
            <TabsTrigger value="OFFER_ACTIVATIONS">Offer activations</TabsTrigger>
          </TabsList>

          <TabsContent value="OFFER_MATCHES">
        <Card>
          <CardContent>
            <DataTable
              :headers="identifedOfferHeaders"
              :items="missedTransactionQuery.identifiedProgramOffer"
              :loading="loading"
              :error="error"
              @retry="getSupportTicket(ticketId)"
              :hide-footer="true"
            >
            </DataTable>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="TRANSACTION_DATA">
        <Card>
          <CardContent>
            <DataTable
              :headers="transactionDataHeaders"
              :items="transactionData"
              :loading="loading"
              :error="error"
              @retry="getSupportTicket(ticketId)"
              :hide-footer="true"
            >
              <template #item-createdOn="createdOn">
                <span class="text-xs whitespace-nowrap">{{ niceDateTime(createdOn.bookingDateTime) }}</span>
              </template>
              <template #item-id="transaction">
                <CopyId :value="transaction.id" :max-width="120" />
              </template>
              <template #item-bookingDateTime="transaction">
                <span class="text-sm text-muted-foreground whitespace-nowrap">{{ niceDateTime(transaction.bookingDateTime) }}</span>
              </template>
              <template #item-amount="transaction">
                <span class="text-sm tabular-nums whitespace-nowrap">
                  {{ transaction.transactionAmount.currency }}{{ (Math.round(transaction.transactionAmount.amount * 100) / 100).toFixed(2) }}
                </span>
              </template>
              <template #item-retailer="transaction">
                <div class="w-40">
                  <div v-if="transaction.enrichedCreditorData != null" class="flex items-start gap-3">
                    <img
                      v-if="transaction.enrichedCreditorData.logo"
                      :src="transaction.enrichedCreditorData.logo"
                      class="w-12 h-12 rounded border bg-white"
                      alt=""
                    />
                    <div>
                      <p class="font-semibold">{{ transaction.enrichedCreditorData.merchant }}</p>
                      <a
                        v-if="transaction.enrichedCreditorData.website"
                        :href="transaction.enrichedCreditorData.website"
                        target="_blank"
                        class="text-sm text-primary hover:underline"
                      >{{ transaction.enrichedCreditorData.website }}</a>
                    </div>
                  </div>
                </div>
              </template>
              <template #item-customerTransactionId="transaction">
                <CopyId :value="transaction.internalTransactionId" :to="{ path: '/cashback-program/ledger', query: { search: transaction.internalTransactionId } }" :max-width="120" />
              </template>
              <template #item-remittanceInformationUnstructured="transaction">
                <div class="w-64">
                  <p class="text-xs text-muted-foreground">{{ transaction.remittanceInformationUnstructured }}</p>
                </div>
              </template>
              <template #item-internalEndUserId="transaction">
                <CopyId :value="transaction.endUserId" :to="`/enrolled-users/users/${transaction.endUserId}`" :max-width="120" />
              </template>
              <template #item-action="transaction">
                <div class="flex justify-end items-center gap-1">
                  <Button
                    v-if="showDeleteTransactionButton"
                    size="sm"
                    variant="destructive"
                    @click="deleteTransactionFromId(transaction.id)"
                  >Delete</Button>
                </div>
              </template>

              <template #item-offerIds="transaction">
                <div>
                  <div
                    v-for="(id, index) in transaction.offersAvailable?.map((val) => val.id)"
                    :key="index"
                  >
                    <Badge variant="outline">{{ id }}</Badge>
                  </div>
                </div>
              </template>
            </DataTable>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="END_USER_DATA">
        <Card>
          <CardContent>
            <div v-if="endUser">
              <div class="flex items-center gap-1.5 text-sm">
                <span class="text-muted-foreground">End user ID</span>
                <CopyId :value="endUser.endUserId" :to="`/enrolled-users/users/${endUser.endUserId}`" :max-width="200" />
              </div>

              <p class="mt-6 mb-3 text-sm font-semibold text-foreground">Set tiers</p>

              <DataTable
                :headers="tiersHeader"
                :items="listOfTiers"
                :loading="loading"
                :error="error"
                @retry="getSupportTicket(ticketId)"
                :hide-footer="true"
              >
                <template #item-id="{ id }">{{ id }}</template>
              </DataTable>

              <p class="mt-6 mb-3 text-sm font-semibold text-foreground">Set offers</p>

              <DataTable
                :headers="offerHeader"
                :items="listOfAssignedOffers"
                :loading="loading"
                :error="error"
                @retry="getSupportTicket(ticketId)"
                :hide-footer="true"
              >
              </DataTable>
            </div>
            <p v-else class="text-sm text-muted-foreground py-6 text-center">
              This end user is not enrolled, so there is no user data to show.
            </p>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="CASHBACK_DATA">
        <Card>
          <CardContent>
            <p class="mb-3 text-sm font-semibold text-foreground">Cashback objects generated</p>
            <DataTable
              :headers="cashbackTable"
              :items="missedTransactionQuery.cashbackObjectListGeneratedOrginally"
              :loading="loading"
              :error="error"
              @retry="getSupportTicket(ticketId)"
              :hide-footer="true"
            >
              <template #item-id="id">
                <CopyId :value="id.id" :max-width="120" />
              </template>
              <template v-slot:[`item-transactionData.internalTransactionId`]="cashback">
                <CopyId
                  v-if="cashback.transactionData"
                  :value="cashback.transactionData.internalTransactionId"
                  :to="{ path: '/cashback-program/ledger', query: { search: cashback.transactionData.internalTransactionId } }"
                  :max-width="120"
                />
                <span v-else class="text-muted-foreground">—</span>
              </template>
              <template #item-createdOn="createdOn">
                <span class="text-xs whitespace-nowrap">{{ niceDateTime(createdOn.createdOn) }}</span>
              </template>

              <template #item-viewOffer="offer">
                <Button
                  size="sm"
                  variant="outline"
                  @click="openOfferInNewTab(offer.programOffer.retailer.id, offer.programOffer.offer.id)"
                >view</Button>
              </template>

              <template #item-status="status">
                <div class="flex items-center gap-2">
                  <Badge v-if="status.status === 'PAID'" variant="success">{{ status.status }}</Badge>
                  <Badge v-else-if="status.status === 'DECLINED'" variant="destructive">{{ status.status }}</Badge>
                  <Badge v-else variant="warning">{{ status.status }}</Badge>

                  <button
                    v-if="showTestCashbackButton"
                    type="button"
                    class="text-xs font-medium text-primary underline-offset-2 hover:underline whitespace-nowrap"
                    @click="() => { showManualCashbackUpdate = true; selectedCashback = status.id }"
                  >Update status</button>
                </div>
              </template>
              <template #item-internalEndUserId="internalEndUserId">
                <CopyId :value="internalEndUserId.endUserId" :to="`/enrolled-users/users/${internalEndUserId.endUserId}`" :max-width="120" />
              </template>
              <template #item-retailer="cashback">
                <div v-if="cashback.programOffer" class="flex items-center gap-2">
                  <img
                    v-if="cashback.programOffer.retailer.logo"
                    :src="cashback.programOffer.retailer.logo"
                    class="w-6 h-6 rounded object-contain border bg-white"
                    alt=""
                  />
                  <span class="text-sm font-medium truncate">{{ cashback.programOffer.retailer.retailerName }}</span>
                </div>
              </template>
              <template #item-transactionValue="cashback">
                <span
                  v-if="cashback.transactionData.transactionAmount != null"
                  class="text-sm tabular-nums whitespace-nowrap"
                >
                  {{ cashback.transactionData.transactionAmount.currency }}{{ (Math.round(cashback.transactionData.transactionAmount.amount * 100) / 100).toFixed(2) }}
                </span>
                <span v-else class="text-sm text-muted-foreground whitespace-nowrap">Affiliate Transaction</span>
              </template>
              <template #item-userReceivable="cashback">
                <span class="text-sm tabular-nums whitespace-nowrap">
                  {{ cashback.payoutData.currency }}{{ (Math.round(cashback.payoutData.endUserCashbackReceivable * 100) / 100).toFixed(2) }}
                </span>
              </template>
              <template #item-publisherReceivable="cashback">
                <span class="text-sm tabular-nums whitespace-nowrap">
                  {{ cashback.payoutData.currency }}{{ (Math.round(cashback.payoutData.publisherCashbackReceivable * 100) / 100).toFixed(2) }}
                </span>
              </template>
              <template #item-declinedReason="cashback">
                <Badge
                  v-if="cashback.declinedReason"
                  variant="destructive"
                >{{ cashback.declinedReason }}</Badge>
              </template>
            </DataTable>

            <p class="mt-6 mb-3 text-sm font-semibold text-foreground">Newly generated cashback objects</p>
            <DataTable
              :headers="cashbackTable"
              :items="missedTransactionQuery.cashbackObjectsGeneratedOnReGeneration"
              :loading="loading"
              :error="error"
              @retry="getSupportTicket(ticketId)"
              :hide-footer="true"
            >
              <template #item-id="id">
                <CopyId :value="id.id" :max-width="120" />
              </template>
              <template v-slot:[`item-transactionData.internalTransactionId`]="cashback">
                <CopyId
                  v-if="cashback.transactionData"
                  :value="cashback.transactionData.internalTransactionId"
                  :to="{ path: '/cashback-program/ledger', query: { search: cashback.transactionData.internalTransactionId } }"
                  :max-width="120"
                />
                <span v-else class="text-muted-foreground">—</span>
              </template>
              <template #item-createdOn="createdOn">
                <span class="text-xs whitespace-nowrap">{{ niceDateTime(createdOn.createdOn) }}</span>
              </template>
              <template #item-viewOffer="offer">
                <Button
                  size="sm"
                  variant="outline"
                  @click="openOfferInNewTab(offer.programOffer.retailer.id, offer.programOffer.offer.id)"
                >view</Button>
              </template>
              <template #item-status="status">
                <div class="flex items-center gap-2">
                  <Badge v-if="status.status === 'PAID'" variant="success">{{ status.status }}</Badge>
                  <Badge v-else-if="status.status === 'DECLINED'" variant="destructive">{{ status.status }}</Badge>
                  <Badge v-else variant="warning">{{ status.status }}</Badge>

                  <button
                    v-if="showTestCashbackButton"
                    type="button"
                    class="text-xs font-medium text-primary underline-offset-2 hover:underline whitespace-nowrap"
                    @click="() => { showManualCashbackUpdate = true; selectedCashback = status.id }"
                  >Update status</button>
                </div>
              </template>
              <template #item-internalEndUserId="internalEndUserId">
                <CopyId :value="internalEndUserId.endUserId" :to="`/enrolled-users/users/${internalEndUserId.endUserId}`" :max-width="120" />
              </template>
              <template #item-retailer="cashback">
                <div v-if="cashback.programOffer" class="flex items-center gap-2">
                  <img
                    v-if="cashback.programOffer.retailer.logo"
                    :src="cashback.programOffer.retailer.logo"
                    class="w-6 h-6 rounded object-contain border bg-white"
                    alt=""
                  />
                  <span class="text-sm font-medium truncate">{{ cashback.programOffer.retailer.retailerName }}</span>
                </div>
              </template>
              <template #item-transactionValue="cashback">
                <span
                  v-if="cashback.transactionData.transactionAmount != null"
                  class="text-sm tabular-nums whitespace-nowrap"
                >
                  {{ cashback.transactionData.transactionAmount.currency }}{{ (Math.round(cashback.transactionData.transactionAmount.amount * 100) / 100).toFixed(2) }}
                </span>
                <span v-else class="text-sm text-muted-foreground whitespace-nowrap">Affiliate Transaction</span>
              </template>
              <template #item-userReceivable="cashback">
                <span class="text-sm tabular-nums whitespace-nowrap">
                  {{ cashback.payoutData.currency }}{{ (Math.round(cashback.payoutData.endUserCashbackReceivable * 100) / 100).toFixed(2) }}
                </span>
              </template>
              <template #item-publisherReceivable="cashback">
                <span class="text-sm tabular-nums whitespace-nowrap">
                  {{ cashback.payoutData.currency }}{{ (Math.round(cashback.payoutData.publisherCashbackReceivable * 100) / 100).toFixed(2) }}
                </span>
              </template>
              <template #item-declinedReason="cashback">
                <Badge
                  v-if="cashback.declinedReason"
                  variant="destructive"
                >{{ cashback.declinedReason }}</Badge>
              </template>
            </DataTable>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="OFFER_ACTIVATIONS">
        <Card>
          <CardContent>
            <DataTable
              :headers="offerActivationHeaders"
              :items="endUserActivations"
              :loading="loading"
              :error="error"
              @retry="getSupportTicket(ticketId)"
              :hide-footer="true"
            >
              <template #item-id="transaction">
                <CopyId :value="transaction.id" :max-width="120" />
              </template>
              <template #item-activationDate="transaction">
                <span class="text-xs whitespace-nowrap">{{ niceDateTime(transaction.activationDate) }}</span>
              </template>
              <template #item-expiryDate="transaction">
                <span class="text-xs whitespace-nowrap">{{ niceDateTime(transaction.expiryDate) }}</span>
              </template>
              <template #item-currentlyActive="transaction">
                <span class="text-xs whitespace-nowrap">{{ checkExpiry(transaction.expiryDate) }}</span>
              </template>
            </DataTable>
          </CardContent>
        </Card>
          </TabsContent>
        </Tabs>
      </TabsContent>
    </Tabs>
  </div>
  <div v-else class="space-y-4">
    <Skeleton class="h-8 w-1/3" />
    <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Skeleton class="h-24" />
      <Skeleton class="h-24" />
      <Skeleton class="h-24" />
      <Skeleton class="h-24" />
    </div>
    <Skeleton class="h-64" />
    <Skeleton class="h-96" />
  </div>
</template>

<script setup></script>

<script>
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardAction,
  Button,
  Badge,
  Input,
  Skeleton,
  CopyId,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@boogi/ui'
import { Plus, Store, Download } from 'lucide-vue-next'
import { DateTime } from 'luxon'
import { DataTable } from '@boogi/ui'

export default {
  name: 'support-ticket',
  components: {
    Card,
    CardHeader,
    CardTitle,
    CardDescription,
    CardContent,
    CardAction,
    Button,
    Badge,
    Input,
    Skeleton,
    CopyId,
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerDescription,
    DrawerFooter,
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
    Plus,
    Store,
    Download,
    DataTable,
  },
  props: {},

  data() {
    return {
      ticketId: this.$route.params.ticketId,
      missedTransactionQuery: undefined,
      expectedOffer: undefined,
      transactionData: [],
      cashbackObjectList: [],
      endUser: undefined,
      endUserActivations: [],
      actionLog: [],
      selectedDataItem: 'TRANSACTION_DATA',

      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength: undefined,
      showFilters: false,
      showErrorModal: false,
      showSuccessModal: false,
      showFormModal: false,
      ticket: [],

      selectedRetailer: undefined,
      loading: true,
      error: false,

      offerActivationHeaders: [
        { text: 'Id', value: 'id' },
        { text: 'Activation Date', value: 'activationDate' },
        { text: 'Expiry Date', value: 'expiryDate' },
        { text: 'Currently Active', value: 'currentlyActive' },
      ],

      identifedOfferHeaders: [
        { text: 'Id', value: 'id' },
        { text: 'Retailer', value: 'retailer.retailerName' },
      ],

      cashbackTable: [
        { text: 'Date created', value: 'createdOn' },
        { text: 'Id', value: 'id' },
        { text: 'Transaction Id', value: 'transactionData.internalTransactionId' },
        { text: 'User id', value: 'internalEndUserId' },
        { text: 'Status', value: 'status' },
        { text: 'Retailer', value: 'retailer' },
        { text: 'Offer Id', value: 'programOffer.id' },
        { text: 'View Offer', value: 'viewOffer' },

        { text: 'Transaction Value', value: 'transactionValue' },
        { text: 'End user receivable', value: 'userReceivable' },
        { text: 'Publisher receivable', value: 'publisherReceivable' },
        { text: 'Declined Reason', value: 'declinedReason' },
      ],

      tiersHeader: [{ text: 'Tier id', value: 'name' }],
      offerHeader: [{ text: 'Offer id', value: 'name' }],

      transactionDataHeaders: [
        { text: 'Transaction id', value: 'customerTransactionId' },
        { text: 'Booking date', value: 'createdOn' },

        { text: 'End user Id', value: 'internalEndUserId' },
        { text: 'Mid', value: 'additionalCardData.mid' },
        { text: 'Enriched data', value: 'retailer' },
        { text: 'Offer Matches', value: 'offerIds' },
        { text: 'Amount', value: 'amount' },
        { text: 'Input description', value: 'unEnrichedCreditorName' },
      ],

      channelsTable: [
        { text: 'Channel name', value: 'channelName' },
        { text: 'Address', value: 'address' },
        { text: 'City', value: 'city' },
        { text: 'Postcode', value: 'postcode' },
        { text: 'Channel Type', value: 'channelType' },
        { text: 'Mids', value: 'mids' },
      ],

      headersTable1: [
        { text: 'Date', value: 'timestamp' },
        { text: 'Action', value: 'action' },
        { text: 'Notes', value: 'notes' },
      ],

      supportAction: {
        actions: undefined,
        notes: '',
      },

      retailers: [],
      orderByList: [
        { id: 'NO_ISSUE_IDENTIFIED', text: 'No issue identified' },
        { id: 'IDENTIFIED_MISSING_OFFER_DATA', text: 'Identified missing offer data' },
        { id: 'IDENTIFIED_TECHNICAL_ISSUE', text: 'Identified a technical issue' },
        { id: 'RE_RUN_TRANSACTION', text: 'Re-run the transaction' },
        { id: 'GENERATE_CASHBACK', text: 'Generate cashback' },
        { id: 'RELEASE_CASHBACK', text: 'Release cashback' },
        { id: 'RESOLVED_QUERY', text: 'Mark query as resolved' },
        { id: 'ESCALATED_TO_CASHBACK_TECHNOLOGY_PROVIDER', text: 'Escalated to Boogi' },
      ],
      offerType: [],
      orderBy: 'MOSTPOPULAR',
    }
  },
  created() {
    this.getSupportTicket(this.ticketId)
  },
  methods: {
    actionLabel(action) {
      const match = this.orderByList.find((o) => o.id === action)
      return match ? match.text : action
    },

    formatRate(earning, amount) {
      if (amount == null) return null
      const type = earning.conditions[0].value.type
      const prefix = type === 'PERCENTAGE' ? '' : this.getCurrencySymbol(this.expectedOffer.offer.currencyList)
      const suffix = type === 'PERCENTAGE' ? '%' : ''
      return `${prefix}${amount.toFixed(2)}${suffix}`
    },

    spendCondition(earning) {
      const c = earning.conditions[0]
      const sym = this.getCurrencySymbol(this.expectedOffer.offer.currencyList)
      if (c.type === 'GREATER_THAN') return `Greater than ${sym}${c.lowerBound}`
      if (c.type === 'LESS_THAN') return `Less than ${sym}${c.lowerBound}`
      if (c.type === 'BETWEEN') return `Between ${sym}${c.lowerBound} and ${sym}${c.upperBound}`
      return `${sym}${c.lowerBound}`
    },

    formatOfferType(t) {
      return ({
        CARD_LINKED: 'Card-linked',
        AFFILIATE: 'Affiliate',
        IN_STORE: 'In-store',
        ONLINE: 'Online',
        PAYMENT: 'Payment-linked',
        CLICK_TO_ACTIVATE: 'Click to activate',
      })[t] || t
    },

    downloadLogs() {
      const csvRows = []
      // Header
      csvRows.push(['Timestamp', 'Action', 'Notes'])
      // Rows
      this.actionLog.forEach((row) => {
        csvRows.push([row.timestamp, row.action, `"${row.notes.replace(/"/g, '""')}"`])
      })

      // Convert to CSV string
      const csvContent = csvRows.map((e) => e.join(',')).join('\n')

      // Trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `action_logs_${this.missedTransactionQuery.id}.csv`
      link.click()
    },

    // An offer without a currency list used to fall into the catch and return the raw
    // (null) argument, which rendered as the literal text "null" — e.g. "Greater than null0".
    getCurrencySymbol(currencyCode, locale = 'en-US') {
      const code = Array.isArray(currencyCode) ? currencyCode[0] : currencyCode
      if (!code) return ''
      try {
        return (0)
          .toLocaleString(locale, {
            style: 'currency',
            currency: code,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
          .replace(/\d/g, '')
          .trim() // strip digits, leave symbol
      } catch (e) {
        return ''
      }
    },

    checkExpiry(dateObject) {
      const now = DateTime.now()
      const expiry = DateTime.fromMillis(dateObject)
      return now < expiry
    },

    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
    },

    openOfferInNewTab(retailerId, offerId) {
      // Ensure code runs only on client side
      if (typeof window !== 'undefined') {
        const url = `/retailer/${retailerId}/offer/${offerId}`
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        console.warn('Window is not defined or expectedOffer is missing')
      }
    },

    async addSupportTicketAction() {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false
      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/support/${this.ticketId}`,
          {
            method: 'POST',
            body: JSON.stringify(this.supportAction),
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        this.supportAction = {
          actions: undefined,
          notes: '',
        }

        if (response.ok) {
          const object = await response.json()
          this.applyTicketResponse(object)
        } else {
          this.error = true
        }
      } catch (error) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    applyTicketResponse(object) {
      this.missedTransactionQuery = object.missedTransactionQuery
      // A missed-transaction ticket often has no enriched transaction yet, and the end user may
      // not be enrolled. Both come back null in that case, so fall back to empty values rather
      // than letting the template dereference null.
      this.transactionData = object.transactionData ? [object.transactionData] : []
      this.cashbackObjectList = object.cashbackObjectList
      this.endUser = object.endUser
      this.endUserActivations = object.endUserOfferActivations
      this.expectedOffer = object.missedTransactionQuery.programOfferRequested

      this.actionLog = Object.entries(object.missedTransactionQuery.actionLog)
        .map(([timestamp, data]) => {
          const dt = new Date(timestamp) // JS Date can parse your format
          return {
            timestamp: isNaN(dt)
              ? timestamp
              : DateTime.fromJSDate(dt).toFormat('yyyy-LL-dd HH:mm:ss'),
            sortDate: isNaN(dt) ? null : dt.getTime(), // keep a raw numeric value for sorting
            action: data.actions,
            notes: data.notes,
          }
        })
        .sort((a, b) => (b.sortDate || 0) - (a.sortDate || 0)) // ascending order
    },

    async getSupportTicket(id) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false
      try {
        const response = await this.$apiFetch(`${apiUrl}/private/api/client/support/${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          const object = await response.json()
          this.applyTicketResponse(object)
        } else {
          this.error = true
        }
      } catch (error) {
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
  computed: {
    listOfTiers() {
      return (this.endUser?.customerTiers ?? []).map((item, index) => ({
        id: index + 1,
        name: item,
      }))
    },
    listOfAssignedOffers() {
      return (this.endUser?.assignedProgramOffers ?? []).map((item, index) => ({
        id: index + 1,
        name: item,
      }))
    },
  },
  mounted() {
    document.title = 'Dashboard | Support tickets'
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getSupportTickets(val.page - 1, val.rowsPerPage)
      },
    },
    activateOfferRequest: {
      deep: true,
      handler: function (newValue) {
        if (newValue.splitOfCashbackPercentage.endUser == '') {
          this.activateOfferRequest.splitOfCashbackPercentage.endUser = 0
        }

        if (newValue.splitOfCashbackPercentage.endUser > 100) {
          this.activateOfferRequest.splitOfCashbackPercentage.endUser = 100
          this.pecentageNotRight = 'The percentage needs to be less than or equal to 100'
        } else if (newValue.splitOfCashbackPercentage.endUser < 0) {
          this.activateOfferRequest.splitOfCashbackPercentage.endUser = 0
          this.pecentageNotRight = 'The percentage needs to be greater than or equal to 0'
        }
        this.pecentageNotRight = ''
        this.activateOfferRequest.splitOfCashbackPercentage.client =
          100 - newValue.splitOfCashbackPercentage.endUser
      },
    },
  },
}
</script>
